// ui/composables/useServiceCodes.js
import { computed, ref } from 'vue';

import {
  removeServiceCode as removeServiceCodeUseCase,
  retireServiceCode as retireServiceCodeUseCase,
} from '../../application/useCases/changeServiceCodeLifespan';
import { viewerToday } from './viewerToday';
import { mintServiceCode } from '../../application/useCases/mintServiceCode';
import { SERVICE_CODE_ORIGIN } from '../../domain/constants/serviceCode';
import {
  findManagedCodesMatching,
  findOwnCodeOn,
  findOwnCodesMatching,
} from '../../domain/services/serviceCodeGovernance';
import { findByCodeOn, isBillableOn, isRetiredOn } from '../../domain/services/serviceCodeValidity';
import {
  createServiceCode,
  hasTreatmentServiceReference,
  loadServiceCodes,
  removeServiceCode,
  retireServiceCode,
} from '../../infrastructure/api/serviceCodeApi';

/**
 * The service codes a clinic can work with — ours and its own — and the authoring of the latter.
 *
 * THE CONTEXT'S COMPOSITION ROOT for service codes: the only place binding the Firestore adapters to
 * the repository port, on the same terms as `useChargeMaster` does for sheets. Nothing above this
 * imports `infrastructure/`, and nothing outside the context queries `serviceCodes` directly — a
 * caller doing so would have to know that the collection holds two populations, that neither query
 * reaches both, and that retired and tombstoned codes are in there too. That is exactly the
 * interpretation the published interface exists to own.
 *
 * "THE CATALOG" IS NOW A SUBSET, NOT THE WHOLE. `serviceCodes` here is the union: the managed
 * catalog plus this clinic's org-authored codes. The word *catalog* is reserved for the managed
 * half — which is what customer copy already assumes, since the mint entry sits under "Not in the
 * catalog" — and the union needs no new noun because nothing above this treats the two differently.
 *
 * THE CLINIC IS SUPPLIED PER OPERATION rather than captured at construction, matching
 * `useChargeMaster`: a page creating this in `setup()` may not have its clinic yet, and a composable
 * that captured a null would read an empty list forever.
 *
 * IT READS THE CLOCK ONCE, captured at creation and never re-read. A dialog left open across
 * midnight keeps offering the codes valid on the date it opened on, so what the user is looking at
 * and what a save resolves against cannot disagree. Every rule beneath takes `asOf` as an argument;
 * `viewerToday` exists to be called from exactly this layer.
 *
 * SELECTABLE AND RESOLVABLE ARE DIFFERENT SETS, and conflating them is the mistake this composable
 * exists to prevent. A picker offers what may be billed for the working date; a row displaying an
 * existing service must resolve whatever code it already references, including a retired one. A
 * screen that resolved only from the selectable set would show a blank where a real, correctly
 * recorded historical code belongs — and would look like missing configuration rather than a fault.
 */

/**
 * @param {{ asOf?: string }} [options] - `asOf` defaults to today and is accepted only so a test or
 *   a caller with its own session date can supply one.
 * @returns {Object}
 */
// eslint-disable-next-line import/prefer-default-export
export function useServiceCodes({ asOf = viewerToday() } = {}) {
  /**
   * The port, satisfied by the Firestore adapters. Assembled here rather than imported as a ready
   * object so the binding is visible at the one place that owns it.
   *
   * @type {import('../../application/ports/ServiceCodeRepository.port').ServiceCodeRepository}
   */
  const repository = {
    loadAvailable: loadServiceCodes,
    mint: createServiceCode,
    retire: retireServiceCode,
    remove: removeServiceCode,
    hasTreatmentServiceReference,
  };

  /** @type {import('vue').Ref<import('../../domain/model/ServiceCode.types').ServiceCode[]>} */
  const serviceCodes = ref([]);
  const isLoading = ref(false);
  const hasLoaded = ref(false);

  /**
   * Everything the clinic can reference, unresolved.
   *
   * RE-READABLE, unlike the load-once shape this had while the collection was read-only. Minting or
   * retiring a code changes what the next question resolves to, so the set has to be refreshed after
   * a write rather than assumed static for the life of the composable.
   *
   * @param {{ clinicId: string }} scope
   * @returns {Promise<void>}
   */
  async function load({ clinicId }) {
    isLoading.value = true;
    try {
      serviceCodes.value = await repository.loadAvailable({ clinicId });
      hasLoaded.value = true;
    } finally {
      isLoading.value = false;
    }
  } // load()

  /** Every code by id, retired and tombstoned included — the resolution set, not the picker's. */
  const byId = computed(() => new Map(
    serviceCodes.value.map((serviceCode) => [serviceCode.id, serviceCode]),
  ));

  /**
   * The code a stored reference names, whatever its state.
   *
   * RESOLVES RETIRED AND TOMBSTONED CODES DELIBERATELY. A treatment service pointing at a code
   * retired last year is correctly recorded, and the screen has to be able to say so. Returns null
   * only when the id names nothing at all, which is a dangling reference and a real fault.
   *
   * @param {string|null|undefined} serviceCodeId
   * @returns {import('../../domain/model/ServiceCode.types').ServiceCode|null}
   */
  function codeById(serviceCodeId) {
    if (serviceCodeId === null || serviceCodeId === undefined) {
      return null;
    }
    return byId.value.get(serviceCodeId) ?? null;
  } // codeById()

  /**
   * The codes that may be billed for the working date, ordered for a picker.
   *
   * Sorted by code string, which is how a biller looks one up. Grouping by system was considered and
   * left out: our curated set is small enough to scan, and a biller reads `97153` without caring
   * which published list it came from. Org-authored codes sort in among ours for the same reason —
   * somebody looking for `SC-DIRECT` is looking for a code, not for a provenance.
   */
  const selectableCodes = computed(() => serviceCodes.value
    .filter((serviceCode) => isBillableOn(serviceCode, asOf))
    .sort((a, b) => `${a.code}`.localeCompare(`${b.code}`)));

  /**
   * Is the code this service already references no longer billable for the working date?
   *
   * WHAT A SCREEN WARNS ON, and narrower than "not selectable". A reference to a code that has not
   * started yet is unusual but forward-looking; a reference to a retired one means this service will
   * hold the next time it is billed. Only the second is worth interrupting somebody about.
   *
   * @param {string|null|undefined} serviceCodeId
   * @returns {boolean}
   */
  function isReferenceRetired(serviceCodeId) {
    const serviceCode = codeById(serviceCodeId);

    return serviceCode !== null && isRetiredOn(serviceCode, asOf);
  } // isReferenceRetired()

  /**
   * The one code matching a code STRING for the working date, or null.
   *
   * FOR HUMANS AND MIGRATIONS ONLY — a person typing a code off a payer's letter. Nothing billing-
   * bound resolves by string; a treatment service stores an id, which is the whole reason identity
   * is not the string. Null covers both "no such code on that date" and "two codes claim it", and
   * neither is arbitrated here. A managed code always wins over a clinic's own — see `findByCodeOn`.
   *
   * @param {string} code
   * @returns {import('../../domain/model/ServiceCode.types').ServiceCode|null}
   */
  function findByCode(code) {
    return findByCodeOn(serviceCodes.value, code, asOf);
  } // findByCode()

  /**
   * Managed codes already carrying a string somebody is about to mint — the governance nudge's data.
   *
   * DATE-BLIND, so a code retired at a changeover still surfaces. That is the moment the nudge exists
   * for: the org cannot find `97153` today because ours ended on 31 December, so they mint their own,
   * and every rollup keyed on the code identity quietly stops seeing them.
   *
   * IT ADVISES AND NEVER BLOCKS. A genuinely local code that happens to collide, and an org whose
   * funder really does bill a code we govern, are both legitimate — so this returns findings and the
   * form decides how loudly to say them.
   *
   * @param {string} code
   * @returns {import('../../domain/model/ServiceCode.types').ServiceCode[]}
   */
  function managedCodesMatching(code) {
    return findManagedCodesMatching(serviceCodes.value, code);
  } // managedCodesMatching()

  /**
   * Every code of the clinic's own already carrying this string.
   *
   * CONTEXT, NOT A REFUSAL. A second period under one string is legitimate — a funder revising what
   * a code covers reuses it — so this is what the form shows the user so they can tell their own
   * revision from their own mistake.
   *
   * @param {string} code
   * @returns {import('../../domain/model/ServiceCode.types').ServiceCode[]}
   */
  function ownCodesMatching(code) {
    return findOwnCodesMatching(serviceCodes.value, code);
  } // ownCodesMatching()

  /**
   * The clinic's own code with this string AND this start date — the one that cannot be written.
   *
   * THE BLOCKING FINDING, and no wider than the document id collides on. Saying so while the user is
   * still filling the form beats a rejected write after they have finished it.
   *
   * @param {string} code
   * @param {string} validFrom - 'YYYY-MM-DD'
   * @returns {import('../../domain/model/ServiceCode.types').ServiceCode|null}
   */
  function ownCodeOn(code, validFrom) {
    return findOwnCodeOn(serviceCodes.value, code, validFrom);
  } // ownCodeOn()

  /** Is this code one the clinic authored? False for a managed code and for an unknown id. */
  function isOwnCode(serviceCodeId) {
    return codeById(serviceCodeId)?.origin === SERVICE_CODE_ORIGIN.CUSTOM;
  } // isOwnCode()

  /**
   * Re-reads after a write, WITHOUT letting the read's failure impersonate the write's.
   *
   * A REFRESH THAT FAILS AFTER A SUCCESSFUL WRITE MUST NOT REJECT. Firestore already holds the
   * change, so reporting it as a failed mutation tells the caller the opposite of the truth — and
   * for a mint it also loses the new id, which is the one thing the caller needs. Worse, a retry
   * then finds the document the first attempt wrote and reports `ServiceCodeAlreadyExists`, so the
   * user is told they have a duplicate of a code they just created successfully.
   *
   * The cost of swallowing it is a stale local copy, which the next read fixes and which the hosts
   * here re-read anyway. That is strictly smaller than a lie about whether the write happened.
   */
  const refreshAfterWrite = async (clinicId) => {
    try {
      await load({ clinicId });
    } catch (error) {
      console.error('Failed to re-read service codes after a successful write', error);
    }
  };

  /**
   * Mints one org-authored code and re-reads, so the caller can resolve it immediately.
   *
   * THE RE-READ IS PART OF THE OPERATION rather than the caller's job. A minted code is almost
   * always about to be referenced by the row the same gesture creates, and a caller that had to
   * remember to reload would render that row as an unknown code until it did. It is not part of the
   * RESULT, though — see `refreshAfterWrite`.
   *
   * @param {import('../../application/ports/ServiceCodeRepository.port').MintServiceCodeRequest} request
   * @returns {Promise<string>} the new code's id
   */
  async function mint(request) {
    const serviceCodeId = await mintServiceCode(request, repository);

    await refreshAfterWrite(request.clinicId);

    return serviceCodeId;
  } // mint()

  /**
   * Closes an org's own code from a date, or reopens one. Null un-retires.
   *
   * @param {{ serviceCodeId: string, clinicId: string, retiredOn: string|null }} request
   * @returns {Promise<void>}
   */
  async function retire(request) {
    await retireServiceCodeUseCase(request, repository);
    await refreshAfterWrite(request.clinicId);
  } // retire()

  /**
   * Disowns a code that should never have existed — a tombstone, never a hard delete.
   *
   * @param {{ serviceCodeId: string, clinicId: string, isSessionMinted: boolean,
   *   hasChargeMasterReference: boolean }} request
   * @returns {Promise<void>}
   */
  async function remove(request) {
    await removeServiceCodeUseCase(request, repository);
    await refreshAfterWrite(request.clinicId);
  } // remove()

  /*
   * NO `unitBasisFor` HELPER. A caller that needs a code's unit basis reads it off the code —
   * `codeById(id).unitBasis` — and a wrapper keyed on a treatment service's reference would put the
   * concept back on the service side, which is the misplacement moving it to the code corrected.
   */

  return {
    asOf,
    serviceCodes,
    isLoading,
    hasLoaded,
    load,
    codeById,
    selectableCodes,
    isReferenceRetired,
    findByCode,
    managedCodesMatching,
    ownCodesMatching,
    ownCodeOn,
    isOwnCode,
    mint,
    retire,
    remove,
  };
} // useServiceCodes()
